import {
    BaseQueryFn,
    createApi,
    fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { getAccessToken, loadingAccessToken, logOut } from './AuthSlice';
import median from 'median-js-bridge';
import { Mutex } from 'async-mutex'; //https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#preventing-multiple-unauthorized-errors
import {
    JobAssignee,
    ApproveReservation,
    BookingArgs,
    BulkActionResponse,
    CancelReservation,
    Channel,
    ChatEnquiry,
    ContractorNotes,
    CustomServiceValidation,
    CustomServiceValidationRequest,
    DeclineReservation,
    Earnings,
    GuestInvoice,
    InviteContractor,
    Job,
    JobChat,
    JobCompletion,
    JobRequest,
    JoinBookingChannel,
    ModuleOccupancy,
    OwnerPreferences,
    PricingTemplateOrderRequest,
    RecurringJob,
    ReservationChat,
    UpdateJob,
    UserPreferences,
    XeroAccount,
    XeroConnection,
    XeroConsentUrl,
    XeroSettings,
    JoinJobChannel,
    NiroJobAccess,
    NirovisionAccessRequest,
    NirovisionAccessMode,
    NirovisionAccessRequestAction,
    NirovisionAccessRequestFace,
    NirovisionAccessRequestUser,
    ReassignReservation,
    ApplyDiscountToBooking,
    ContractorPreferences,
    UpdateWaitListRequest,
    UploadGuideRequest,
    WaitListRequestDetailed,
    ExperienceResponse,
    CreateExperience,
    UpdateExperience,
    GetIndividualOwnerAssetsParams,
    UpdateOwnerProfileRequest,
} from './SevenDayApiTypes';
import { Department } from '../../feature/types/department';
import { Period } from '../../feature/enums/reports';
import { MessageTemplate } from '../../feature/types/messaging';
import { Media, User } from '../../feature/types/user';
import {
    Contractor,
    OwnerContractor,
    PricingTemplate,
} from '../../feature/types/contractor';
import {
    BookingSetMenu,
    SetMenu,
    UpdateBookingSetMenu,
} from '../../feature/types/set-menu';
import { ROLE_OPTIONS } from '../../feature/components/User Management/Access Control/VIPModal';
import { CreditHold } from '../../feature/types/credit-hold';
import {
    RsvpConfig,
    RsvpRequest,
    RsvpStatus,
} from '../../feature/types/reservation';
import {
    ItineraryItemAttachment,
    ItineraryItemDto,
    ItineraryItemPayload,
} from '../../feature/types/itinerary';
import {
    CreateDevice,
    Device,
    UpdateDevice,
    Vehicle,
} from '../../feature/types/vehicles';
import { Experience } from '../../feature/types/experience';
import {
    ExpenseStats,
    ExpenseStatsArgs,
    AddOnStats,
    AddOnStatsArgs,
    JobStats,
    JobStatsArgs,
    RsvpSentStats,
    RsvpSentStatsArgs,
    RsvpResponseStats,
    RsvpResponseStatsArgs,
    GuestUserStats,
    ExternalGuestAttendeeStats,
    ExternalGuestAttendeeStatsArgs,
    TotalBookingsStats,
    NewBookingStats,
    NewBookingStatsArgs,
} from '../../feature/types/dashboard';
import { ReleaseSettings } from '../../feature/types/release-notes';
import {
    GuestProfile,
    GuestReview,
    GuestUserProfile,
    UserReview,
} from '../../feature/types/review';
import { OwnerProfile } from '../../feature/types/owner';

type WishlistModule = {
    id: number;
    name: string;
    type: string;
};

const { REACT_APP_BASE_URL } = process.env;
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
    baseUrl: REACT_APP_BASE_URL,
    mode: 'cors',
    credentials: 'include',
    // Some backend endpoints (notably PATCH edit flows) may respond with 304 even
    // when the update succeeded. Treat 304 as a successful response so RTKQ
    // doesn't surface it as a network error in the UI.
    validateStatus: (response) =>
        (response.status >= 200 && response.status < 300) ||
        response.status === 304,
    prepareHeaders: (headers, { getState }) => {
        const token = getAccessToken(getState());
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

let refreshResult: any;
const refreshQuery = async (args: any, api: any, extraOptions: any) => {
    if (!mutex.isLocked()) {
        const release = await mutex.acquire();
        try {
            // send refresh token to get new access token
            refreshResult = await baseQuery(
                { url: '/refresh', method: 'POST' },
                api,
                extraOptions,
            );
            if (refreshResult.data) {
                // store the new token
                api.dispatch(loadingAccessToken(refreshResult.data));
            } else {
                // Logout if refresh token is expired
                api.dispatch(logOut());
            }
            return refreshResult;
        } finally {
            release();
        }
    } else {
        await mutex.waitForUnlock();
        return refreshResult;
    }
};

const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
    await mutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    // Get a new access token if it is expired
    if (
        result?.error?.status === 401 &&
        (result.error.data as { message: string }).message === 'Unauthorized'
    ) {
        const refreshResult = await refreshQuery({}, api, extraOptions);
        if (refreshResult.data) {
            // retry the original query with new access token
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};
export const sevenDayApi = createApi({
    reducerPath: 'SevenDayApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'Assets',
        'BookingCaps',
        'SignIn',
        'Profile',
        'Owners',
        'Guest',
        'Users',
        'Clients',
        'Reservations',
        'Reports',
        'Fees',
        'Teams',
        'Policies',
        'Chat',
        'Chat.Templates',
        'AI',
        'Platform',
        'Release',
        'Billing',
        'Discounts',
        'XeroSettings',
        'Settings',
        'Contractors',
        'Jobs',
        'Nirovision',
        'ExpenseClaims',
        'PayoutDetails',
        'accessControl',
        'billingexclusions',
        'UserReview',
        'GuestReview',
        'WaitListRequests',
        'moduleAccess',
        'CreditHolds',
        'Devices',
        'Vehicles',
        'Wishlist',
        'SetMenus',
        'BookingSetMenus',
    ],
    endpoints: (builder) => ({
        getAllAssets: builder.query<any[], any>({
            query: ({ url, ...params }) => ({ url, params }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'SignIn'],
        }),
        getWishlist: builder.query<WishlistModule[], void>({
            query: () => '/modules/wishlist/list',
            providesTags: ['Wishlist'],
        }),
        addToWishlist: builder.mutation<WishlistModule[], number>({
            query: (moduleId) => ({
                url: `/modules/wishlist/${moduleId}`,
                method: 'POST',
            }),
            invalidatesTags: ['Wishlist'],
        }),
        removeFromWishlist: builder.mutation<WishlistModule[], number>({
            query: (moduleId) => ({
                url: `/modules/wishlist/${moduleId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Wishlist'],
        }),
        getIndividualOwnerAssets: builder.query<
            any,
            { url: string } & GetIndividualOwnerAssetsParams
        >({
            query: ({ url, ...params }) => ({ url, params }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'SignIn'],
        }),
        getIndividualAsset: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'SignIn'],
        }),
        addAsset: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Assets'],
        }),

        createExperience: builder.mutation<
            ExperienceResponse,
            { ownerId: number; experience: CreateExperience }
        >({
            query: ({ ownerId, experience }) => ({
                url: `/v2/owners/${ownerId}/modules`,
                method: 'POST',
                body: experience,
            }),
            invalidatesTags: ['Assets'],
        }),
        getOwnerExperience: builder.query<
            Experience,
            { owner: number; experience: number }
        >({
            query: ({ owner, experience }) =>
                `/owners/${owner}/modules/${experience}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'SignIn'],
        }),
        getOwnerExperiences: builder.query<
            Experience[],
            { owner: number } & GetIndividualOwnerAssetsParams
        >({
            query: ({ owner, ...params }) => {
                return { url: `/owners/${owner}/modules`, params };
            },
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'SignIn'],
        }),
        publishExperience: builder.mutation<
            Experience,
            { owner: number; experience: number }
        >({
            query: ({ owner, experience }) => ({
                url: `/owners/${owner}/modules/${experience}/publish`,
                method: 'POST',
            }),
            invalidatesTags: ['Assets', 'SignIn'],
        }),

        updateExperience: builder.mutation<
            Experience,
            {
                owner: number;
                id: number;
                experience: UpdateExperience;
            }
        >({
            query: ({ owner, id, experience }) => ({
                url: `/owners/${owner}/modules/${id}`,
                method: 'PATCH',
                body: experience,
            }),
            invalidatesTags: ['Assets', 'SignIn'],
        }),

        addPhotos: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/photos`,
                method: 'POST',
                body: data[1],
                // headers: {
                //     "Content-Type": "multipart/form-data",
                //   }
            }),
            // invalidatesTags: ['Assets', 'SignIn'],
        }),
        addVideoThumbnail: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/video-thumbnails`,
                method: 'POST',
                body: data[1],
            }),
        }),

        addGuideThumbnail: builder.mutation<Media, [number, FormData]>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/guide-thumbnails`,
                method: 'POST',
                body: data[1],
            }),
        }),

        addPresignedVideoUrl: builder.mutation<
            { id: string; uploadUrl: string; fileUrl: string },
            { ownerId: string; fileName: string; contentType: string }
        >({
            query: ({ ownerId, fileName, contentType }) => ({
                url: `/owners/${ownerId}/modules/presign`,
                method: 'POST',
                body: { fileName, contentType },
            }),
        }),

        addPresignedGuideUrl: builder.mutation<
            { id: string; uploadUrl: string; fileUrl: string },
            { ownerId: string; fileName: string; contentType: string }
        >({
            query: ({ ownerId, fileName, contentType }) => ({
                url: `/owners/${ownerId}/modules/guides/presign`,
                method: 'POST',
                body: { fileName, contentType },
            }),
        }),

        uploadVideo: builder.mutation<
            {
                id: string;
                src: string;
                thumbnail?: { id: string; src: string };
            },
            {
                ownerId: string;
                id: string;
                fileUrl: string;
                thumbnail?: string;
            }
        >({
            query: ({ ownerId, id, fileUrl, thumbnail }) => ({
                url: `/owners/${ownerId}/modules/video`,
                method: 'POST',
                body: { id, fileUrl, thumbnail },
            }),
        }),

        uploadGuide: builder.mutation<Media, UploadGuideRequest>({
            query: ({ ownerId, ...body }) => ({
                url: `/owners/${ownerId}/modules/guides`,
                method: 'POST',
                body,
            }),
        }),

        addFloorPlans: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/floorPlans`,
                method: 'POST',
                body: data[1],
                // headers: {
                //     "Content-Type": "multipart/form-data",
                //   }
            }),
            // invalidatesTags: ['Assets', 'SignIn'],
        }),
        editPhotos: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/photos`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Assets', 'SignIn'],
        }),
        editAsset: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/${data[1]}`,
                method: 'PATCH',
                body: data[2],
            }),
            invalidatesTags: ['Assets'],
        }),

        deleteAsset: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/${data[1]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Assets'],
        }),
        calculatePrice: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/calculate-price`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: [],
        }),
        getSpaceBookingsCount: builder.mutation<
            { bookingsCount: number },
            [number, number]
        >({
            query: (data) => ({
                url: `/owners/${data[0]}/modules/spaces/${data[1]}/bookings-count`,
                method: 'GET',
            }),
            // providesTags: [],
        }),
        getAssetCalendarAvailability: builder.query<any, any>({
            query: ({ url, ...params }) => ({ url, params }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'Reservations', 'SignIn'],
        }),

        signIn: builder.mutation<any, any>({
            query: (data) => ({
                url: '/signin',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SignIn'],
        }),

        signUp: builder.mutation<any, any>({
            query: (data) => ({
                url: '/guests/sign-up',
                method: 'POST',
                body: data,
            }),
        }),

        refresh: builder.mutation<any, any>({
            queryFn: refreshQuery,
        }),

        signOut: builder.mutation<any, any>({
            queryFn: async (args, api, extraOptions: any) => {
                const result = await baseQueryWithReauth(
                    { url: '/signout', method: 'POST' },
                    api,
                    extraOptions,
                );
                median.onesignal.externalUserId.remove();
                api.dispatch(logOut());
                return result;
            },
        }),

        getIndividualOwner: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Owners', 'SignIn'],
        }),
        getIndividualOwnerUsers: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Owners', 'SignIn'],
        }),
        addIndividualOwnerUsers: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/users`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Users'],
        }),
        addIndividualGuestUsers: builder.mutation<any, any>({
            query: (data) => ({
                url: `/guests/${data[0]}/users`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Users', 'Teams'],
        }),
        addIndividualContractorUsers: builder.mutation<any, any>({
            query: (data) => ({
                url: `/contractors/${data[0]}/users`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Users'],
        }),

        validateImportGuestUsers: builder.mutation<any, any>({
            query: (data) => ({
                url: `/guests/${data[0]}/users/validate-import`,
                method: 'POST',
                body: data[1],
            }),

            invalidatesTags: ['Users', 'Teams', 'Assets'],
        }),
        importGuestUsers: builder.mutation<any, any>({
            query: (data) => ({
                url: `/guests/${data[0]}/users/import`,
                method: 'POST',
                body: data[1],
            }),

            invalidatesTags: ['Users', 'Teams', 'Assets'],
        }),

        editProfileDetails: builder.mutation<any, any>({
            query: (data) => ({
                url: `/profile`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Profile', 'Teams', 'Users'],
        }),
        addIndividual7DayAdmin: builder.mutation<any, any>({
            query: (data) => ({
                url: `/7-day/users`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Users'],
        }),
        addNewClient: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Clients'],
        }),
        getAllClients: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Clients', 'SignIn'],
        }),
        getIndividualClient: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Clients', 'SignIn'],
        }),
        editClient: builder.mutation<any, any>({
            query: (data) => ({
                url: data[0],
                method: 'PATCH',
                body: data[1],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['Clients'],
        }),
        deleteClient: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Clients'],
        }),
        editCompany: builder.mutation<any, [string, any]>({
            query: (data) => ({
                url: data[0],
                method: 'PATCH',
                body: data[1],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['Profile'],
        }),
        getCompanyOwnerInfo: builder.query<any, any>({
            query: (id) => `/owners/${id}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Profile', 'SignIn'],
        }),

        getCompanyGuestsInfo: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Profile', 'SignIn'],
        }),
        getProfileCompanyInfo: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Profile', 'SignIn'],
        }),
        getProfileDetails: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Profile', 'SignIn'],
        }),
        deleteProfilePicture: builder.mutation<any, any>({
            query: () => ({
                url: '/profile/picture',
                method: 'DELETE',
            }),
            invalidatesTags: ['Profile', 'SignIn'],
        }),
        createProfilePicture: builder.mutation<
            Media,
            { data: FormData; token: string }
        >({
            query: ({ data, token }) => ({
                url: 'profile/picture',
                method: 'POST',
                body: data,
                formData: true,
                headers: { 'x-profile-validation-token': token },
            }),
            invalidatesTags: ['Profile', 'SignIn'],
        }),
        validateProfilePicture: builder.mutation<
            { rejectionReasons: string[]; validationToken: string },
            FormData
        >({
            query: (data) => ({
                url: 'profile/picture/validation',
                method: 'POST',
                body: data,
                formData: true,
            }),
        }),
        getUserPreferences: builder.query<UserPreferences, number>({
            query: () => `/users/preferences`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Settings', 'SignIn'],
        }),
        updateUserPreferences: builder.mutation<
            UserPreferences,
            UserPreferences
        >({
            query: (body) => ({
                url: `/users/preferences`,
                method: 'PATCH',
                body: body,
                formData: true,
            }),
            invalidatesTags: ['Settings', 'SignIn'],
        }),
        createChatEnquiry: builder.mutation<Channel, ChatEnquiry>({
            query: (data) => ({
                url: 'chat/enquiry',
                method: 'POST',
                body: data,
            }),
        }),
        createReservationChat: builder.mutation<Channel, ReservationChat>({
            query: (data) => ({
                url: 'chat/booking-message',
                method: 'POST',
                body: data,
            }),
        }),

        joinBookingChannel: builder.mutation<Channel, JoinBookingChannel>({
            query: (data) => ({
                url: 'chat/join-booking-channel',
                method: 'POST',
                body: data,
            }),
        }),

        joinJobChannel: builder.mutation<Channel, JoinJobChannel>({
            query: (data) => ({
                url: 'chat/join-job-channel',
                method: 'POST',
                body: data,
            }),
        }),

        createJobChat: builder.mutation<Channel, JobChat>({
            query: (data) => ({
                url: 'chat/job-channel',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Jobs'],
        }),

        getChatUsers: builder.query<any, any>({
            query: (userIds) => {
                const params = new URLSearchParams();
                userIds.forEach((id: string) => params.append('userIds', id));
                return `/chat/users?${params.toString()}`;
            },
            providesTags: ['Chat', 'SignIn'],
        }),
        getChatToken: builder.query({
            query: () => {
                return `/chat/auth`;
            },
            providesTags: ['SignIn'],
        }),
        getMessageTemplates: builder.query<MessageTemplate[], void>({
            query: () => {
                return `/chat/templates`;
            },
            providesTags: ['Chat.Templates', 'SignIn'],
        }),
        createMessageTemplate: builder.mutation<
            MessageTemplate,
            MessageTemplate
        >({
            query: (data) => ({
                url: '/chat/templates',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Chat.Templates'],
        }),
        updateMessageTemplate: builder.mutation<
            MessageTemplate,
            [number, MessageTemplate]
        >({
            query: (data) => ({
                url: `/chat/templates/${data[0]}`,
                method: 'PATCH',
                body: data[1],
            }),
            invalidatesTags: ['Chat.Templates'],
        }),
        deleteMessageTemplate: builder.mutation<void, number>({
            query: (id) => ({
                url: `/chat/templates/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Chat.Templates'],
        }),
        getUserList: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Users', 'SignIn'],
        }),
        getBookingCapList: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['BookingCaps', 'SignIn'],
        }),
        addIndividualBookingCap: builder.mutation<any, any>({
            query: (data) => ({
                url: `/booking-caps`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['BookingCaps'],
        }),
        getIndividualBookingCap: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['BookingCaps', 'SignIn'],
        }),
        getBookingCapAvailability: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings/checkAvailability`,
                method: 'POST',
                body: data,
            }),
            // invalidatesTags: ['BookingCaps', 'SignIn'],
        }),
        checkSelectedPeriodAvailability: builder.mutation<
            any,
            {
                moduleId: number;
                startDate: string;
                endDate: string;
                checkinTime: string;
                checkoutTime: string;
            }
        >({
            query: (data) => ({
                url: `/modules/check-selected-period-availability`,
                method: 'POST',
                body: data,
            }),
        }),
        editBookingCap: builder.mutation<any, any>({
            query: (data) => ({
                url: `/booking-caps/${data[0]}`,
                method: 'PATCH',
                body: data[1],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['BookingCaps'],
        }),
        deleteBookingCap: builder.mutation<any, any>({
            query: (data) => ({
                url: `/booking-caps/${data[0]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['BookingCaps'],
        }),
        getFeeList: builder.query<any, any>({
            query: (company) => `/owners/${company}/fees`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Fees', 'SignIn'],
        }),
        addIndividualFee: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/fees`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Fees'],
        }),
        getIndividualFee: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Fees', 'SignIn'],
        }),
        editFee: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/fees/${data[1]}`,
                method: 'PATCH',
                body: data[2],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['Fees'],
        }),
        deleteFee: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/fees/${data[1]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Fees'],
        }),
        getTeamList: builder.query<Array<Department>, any>({
            query: (company) => `/departments`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Teams', 'SignIn'],
        }),
        addTeam: builder.mutation<any, any>({
            query: (data) => ({
                url: `/departments`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Teams'],
        }),
        getIndividualTeam: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Teams', 'SignIn'],
        }),
        editTeam: builder.mutation<any, any>({
            query: (data) => ({
                url: `/departments/${data[0]}`,
                method: 'PATCH',
                body: data[1],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['Teams'],
        }),
        deleteTeam: builder.mutation<any, any>({
            query: (data) => ({
                url: `/departments/${data[0]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Teams'],
        }),
        getPolicyList: builder.query<any, any>({
            query: (company) => `/owners/${company}/policies`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Policies', 'SignIn'],
        }),
        getIndividualPolicy: builder.query<any, any>({
            query: (data) => `/owners/${data[0]}/policies/${data[1]}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Policies', 'SignIn'],
        }),
        addPolicy: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/policies`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Policies'],
        }),
        editPolicy: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/policies/${data[1]}`,
                method: 'PATCH',
                body: data[2],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['Policies'],
        }),
        deletePolicy: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/policies/${data[1]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Policies'],
        }),

        getIndividualUserInfo: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Users', 'SignIn'],
        }),
        editUser: builder.mutation<any, any>({
            query: (data) => ({
                url: data[0],
                method: 'PATCH',
                body: data[1],
            }),
            transformResponse: (response: any) => {
                return response;
            },
            invalidatesTags: ['Users', 'Teams'],
        }),
        forgotPassword: builder.mutation<any, any>({
            query: (data) => ({
                url: `/forgot-password`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SignIn'],
        }),
        resendCode: builder.mutation<any, any>({
            query: (data) => ({
                url: `/external-attendees/resend-code`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SignIn'],
        }),
        resetPassword: builder.mutation<any, any>({
            queryFn: async (args, api, extraOptions) => {
                return baseQuery(
                    { url: '/reset-password', method: 'POST', body: args },
                    api,
                    extraOptions,
                );
            },
            invalidatesTags: ['SignIn'],
        }),
        verifyEmail: builder.mutation<any, any>({
            query: (data) => ({
                url: `/verify-email`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SignIn'],
        }),
        sendVerificationEmail: builder.mutation<any, any>({
            query: (data) => ({
                url: `/verification-email`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SignIn'],
        }),
        changePassword: builder.mutation<any, any>({
            query: (data) => ({
                url: `/change-password`,
                method: 'POST',
                body: data,
            }),
        }),
        sendInvite: builder.mutation<any, any>({
            query: (data) => ({
                url: `/send-invitation`,
                method: 'POST',
                body: data,
                // Backend may respond with 200/204 and an empty body; default JSON
                // parsing would fail and surface as a mutation error despite success.
                responseHandler: async (response: Response) => {
                    if (response.status === 204) {
                        return null;
                    }
                    const text = await response.text();
                    if (!text?.trim()) {
                        return null;
                    }
                    try {
                        return JSON.parse(text) as unknown;
                    } catch {
                        return text;
                    }
                },
            }),
            invalidatesTags: ['SignIn', 'Profile', 'Users'],
        }),
        submitReservation: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [
                'Reservations',
                'Assets',
                'SignIn',
                'Reports',
                'Nirovision',
            ],
        }),

        applyDiscountToBooking: builder.mutation<
            any,
            [number, ApplyDiscountToBooking]
        >({
            query: ([id, data]) => ({
                url: `/bookings/${id}/discount`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Reservations', 'Reports'],
        }),

        calculateFees: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings/calculate-charges`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [],
        }),

        getAllReservations: builder.query<any, any>({
            query: (args) => ({ url: '/bookings', params: { ...args } }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reservations', 'SignIn'],
        }),

        getAllReservationsDetailed: builder.query<any, BookingArgs>({
            query: (args) => ({
                url: '/bookings/detailed',
                params: { ...args },
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reservations', 'SignIn'],
        }),

        getAvailability: builder.query<any, any>({
            query: (data) => ({
                url: `${data.url}`,
                method: 'POST',
                body: data.body,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'Reservations', 'SignIn'],
        }),

        getIndividualReservation: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reservations', 'SignIn'],
        }),

        reassignBulkReservations: builder.mutation<
            BulkActionResponse,
            ReassignReservation
        >({
            query: (data) => ({
                url: `/bookings/reassign`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [],
        }),
        // TODO: Add type
        getReservationConflicts: builder.mutation<any[], { bookingId: number }>(
            {
                query: (data) => ({
                    url: `/bookings/get-overlaps`,
                    method: 'POST',
                    body: data,
                }),
                // invalidatesTags: ['SignIn'],
            },
        ),
        approveReservation: builder.mutation<any, any>({
            query: (id) => ({
                url: `/bookings/${id}/approve`,
                method: 'POST',
            }),
            invalidatesTags: ['Reservations', 'Reports'],
        }),
        approveBulkReservations: builder.mutation<
            BulkActionResponse,
            ApproveReservation
        >({
            query: (data) => ({
                url: `/bookings/approve`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [],
        }),
        addOwnerNotes: builder.mutation<
            any,
            { reservationId: string; ownerNotes: string }
        >({
            query: (data) => ({
                url: `/bookings/${data.reservationId}/addOwnerNotes`,
                method: 'POST',
                body: { ownerNotes: data.ownerNotes },
            }),
            invalidatesTags: ['Reservations'],
        }),

        addAttachments: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings/${data[0]}/addAttachments`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Reservations'],
        }),
        deleteAttachment: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings/${data[0]}/attachments/${data[1]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Reservations'],
        }),
        declineReservation: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings/${data[0]}/decline`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Reservations', 'Reports'],
        }),
        declineBulkReservations: builder.mutation<
            BulkActionResponse,
            DeclineReservation
        >({
            query: (data) => ({
                url: `/bookings/decline`,
                method: 'POST',
                body: data,
            }),
            // invalidatesTags: ['Reservations', 'Reports', 'AI'],
        }),
        cancelReservation: builder.mutation<any, any>({
            query: (data) => ({
                url: `/bookings/${data[0]}/cancel`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['Reservations', 'Reports', 'AI'],
        }),
        cancelBulkReservations: builder.mutation<
            BulkActionResponse,
            CancelReservation
        >({
            query: (data) => ({
                url: `/bookings/cancel`,
                method: 'POST',
                body: data,
            }),
            // invalidatesTags: ['Reservations', 'Reports', 'AI'],
        }),
        editReservation: builder.mutation<any, any>({
            query: (data) => ({
                url: data[0],
                method: 'PATCH',
                body: data[1],
            }),
            transformResponse: (response: any) => {
                return response ?? {};
            },
            invalidatesTags: ['Reservations', 'Reports', 'Nirovision'],
        }),
        getAllGuests: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Guest', 'SignIn', 'BookingCaps'],
        }),
        getAllGuestAttendees: builder.query<any, any>({
            query: (company) => `/guests/${company}/users/attendees`,
            providesTags: ['Guest', 'SignIn', 'BookingCaps'],
        }),
        getAllExternalAttendees: builder.query<any, any>({
            query: (company) => `/guests/${company}/users/externalAttendees`,
            providesTags: ['Guest', 'SignIn', 'BookingCaps'],
        }),
        getAllOwnerAttendees: builder.query<User[], number>({
            query: (company) => `/owners/${company}/users/attendees`,
            providesTags: ['Users', 'SignIn'],
        }),
        getIndividualGuests: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Guest', 'SignIn'],
        }),
        getReport: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getExpenseStats: builder.query<ExpenseStats, ExpenseStatsArgs>({
            query: (args) => ({
                url: '/dashboard/expense-stats',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getAddOnStats: builder.query<AddOnStats, AddOnStatsArgs>({
            query: (args) => ({
                url: '/dashboard/add-on-stats',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getJobStats: builder.query<JobStats, JobStatsArgs>({
            query: (args) => ({
                url: '/dashboard/job-stats',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getRsvpSentStats: builder.query<RsvpSentStats, RsvpSentStatsArgs>({
            query: (args) => ({
                url: '/dashboard/rsvp-sent-stats',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getRsvpResponseStats: builder.query<
            RsvpResponseStats,
            RsvpResponseStatsArgs
        >({
            query: (args) => ({
                url: '/dashboard/rsvp-response-stats',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getGuestUserStats: builder.query<GuestUserStats, void>({
            query: () => '/dashboard/guest-user-stats',
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getExternalGuestAttendeeStats: builder.query<
            ExternalGuestAttendeeStats,
            ExternalGuestAttendeeStatsArgs
        >({
            query: (args) => ({
                url: '/dashboard/external-guest-attendee-stats',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getTotalBookingsStats: builder.query<TotalBookingsStats, void>({
            query: () => '/dashboard/total-bookings-stats',
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        getNewBookingStats: builder.query<NewBookingStats, NewBookingStatsArgs>(
            {
                query: (args) => ({
                    url: '/dashboard/new-booking-stats',
                    params: args,
                }),
                transformResponse: (response: any) => {
                    return response;
                },
                providesTags: ['Reports', 'SignIn'],
            },
        ),
        getOccupancy: builder.query<
            ModuleOccupancy[],
            { period: Period; module?: number }
        >({
            query: (args) => ({
                url: '/reports/occupancy',
                params: args,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reports', 'SignIn'],
        }),
        /* ---------- Billing ---------- */

        getBillingReport: builder.query<GuestInvoice[], any>({
            query: (arg) => ({
                url: 'invoices',
                params: arg,
            }),
            providesTags: ['SignIn'],
        }),

        getIndividualBill: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['SignIn'],
        }),
        setBillingAccount: builder.mutation<
            any,
            { guestId: number; billingAccount: any }
        >({
            query: ({ guestId, billingAccount }) => ({
                url: `/guests/${guestId}/billing-account`,
                method: 'PUT',
                body: billingAccount,
            }),
            invalidatesTags: ['SignIn'],
        }),

        getBillingAccount: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Assets', 'SignIn'],
        }),

        /* ---------- AI ---------- */
        getAIConversationId: builder.mutation<any, any>({
            query: () => ({
                url: `/ai/conversations`,
                method: 'POST',
            }),
            invalidatesTags: ['AI'],
        }),
        getAllConversations: builder.query<any, any>({
            query: () => `/ai/conversations`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['SignIn'],
        }),
        getIndividualConversation: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['SignIn'],
        }),
        aiReservationRequest: builder.mutation<any, any>({
            query: (data) => ({
                url: `/ai/bookings`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['AI'],
        }),
        aiGuestReservationRequest: builder.mutation<any, any>({
            query: (data) => ({
                url: `/ai/guest-bookings`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['AI'],
        }),
        validateCustomService: builder.mutation<
            CustomServiceValidation,
            CustomServiceValidationRequest
        >({
            query: (body) => ({
                url: `/ai/contractor-service-validation`,
                method: 'POST',
                body,
            }),
        }),

        submitRecurringReservation: builder.mutation<any, any>({
            query: (data) => ({
                url: `/recurring-bookings`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [
                'Reservations',
                'Assets',
                'SignIn',
                'Reports',
                'Nirovision',
            ],
        }),
        getRecurringReservation: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['AI'],
        }),
        cancelRecurringReservation: builder.mutation<any, any>({
            query: (data) => ({
                url: `/recurring-bookings/${data[0]}/cancel`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['AI', 'Reservations', 'Reports'],
        }),
        aiDiscountsRequest: builder.mutation<any, any>({
            query: (data) => ({
                url: `/ai/discounts`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['AI', 'Discounts'],
        }),
        submitDiscount: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data?.[0]}/discounts`,
                method: 'POST',
                body: data?.[1],
            }),
            invalidatesTags: ['AI', 'Discounts', 'Assets'],
        }),
        getDiscounts: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['AI', 'Discounts'],
        }),
        getIndividualDiscount: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['AI', 'Discounts'],
        }),
        deleteDiscount: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/discounts/${data[1]}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['AI', 'Discounts', 'Assets'],
        }),
        disableDiscount: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/discounts/${data[1]}/toggle-disable`,
                method: 'PATCH',
            }),
            invalidatesTags: ['AI', 'Discounts', 'Assets'],
        }),
        editPlatformSettings: builder.mutation<any, any>({
            query: (data) => ({
                url: `/platform-settings`,
                method: 'PATCH',
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    sevenDayApi.util.updateQueryData(
                        'getPlatformSettings',
                        '/platform-settings',
                        (draft) => {
                            Object.assign(draft, data);
                        },
                    ),
                );
                try {
                    const { data: updated } = await queryFulfilled;
                    if (updated) {
                        dispatch(
                            sevenDayApi.util.updateQueryData(
                                'getPlatformSettings',
                                '/platform-settings',
                                (draft) => {
                                    Object.assign(draft, updated);
                                },
                            ),
                        );
                    }
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: ['Platform'],
        }),
        getPlatformSettings: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Platform', 'SignIn'],
        }),
        getReleaseSettings: builder.query<ReleaseSettings, void>({
            query: () => 'release-settings',
            providesTags: ['Release', 'SignIn'],
        }),
        editReleaseSettings: builder.mutation<ReleaseSettings, ReleaseSettings>(
            {
                query: (data) => ({
                    url: 'release-settings',
                    method: 'PATCH',
                    body: data,
                }),
                invalidatesTags: ['Release'],
            },
        ),
        getXeroConsentUrl: builder.query<XeroConsentUrl, void>({
            query: () => 'xero/consent-url',
        }),
        getXeroConnection: builder.query<XeroConnection, void>({
            query: () => 'xero/connection',
        }),
        getXeroSettings: builder.query<XeroSettings, void>({
            query: () => 'xero/settings',
            providesTags: ['SignIn', 'XeroSettings'],
        }),
        editXeroSettings: builder.mutation<XeroSettings, XeroSettings>({
            query: (data) => ({
                url: `xero/settings`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['XeroSettings'],
        }),
        getXeroAccounts: builder.query<XeroAccount[], void>({
            query: () => 'xero/accounts',
            providesTags: ['SignIn'],
        }),
        getEarnings: builder.query<
            Earnings,
            {
                year: number;
                owner?: number;
                month?: number;
                projected?: boolean;
            }
        >({
            query: (arg) => ({
                url: 'earnings',
                params: arg,
            }),
            providesTags: ['SignIn'],
        }),
        addNirovisionConnection: builder.mutation<any, any>({
            query: (data) => ({
                url: `/nirovision/connection`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Nirovision'],
        }),
        getNirovisionConnection: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Nirovision', 'SignIn'],
        }),
        getNirovisionLocations: builder.query({
            query: () => {
                return `/nirovision/locations`;
            },
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Nirovision'],
        }),
        getNirovisionLabels: builder.query({
            query: () => {
                return `/nirovision/labels`;
            },
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Nirovision'],
        }),
        getNirovisionAccessList: builder.query({
            query: (id) => `/nirovision/access/${id}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Nirovision'],
        }),
        getNirovisionJobAccessList: builder.query({
            query: (id) => `/nirovision/job-access/${id}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Nirovision'],
        }),
        provideNirovisionAccess: builder.mutation<any, any>({
            query: (data) => ({
                url: `/nirovision/provide-access`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Nirovision'],
        }),
        provideNirovisionContractorAccess: builder.mutation<
            void,
            NiroJobAccess
        >({
            query: (data) => ({
                url: `/nirovision/provide-contractor-access`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Nirovision'],
        }),
        removeNirovisionAccess: builder.mutation<any, any>({
            query: (data) => ({
                url: `/nirovision/remove-access`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Nirovision'],
        }),
        removeNirovisionContractorAccess: builder.mutation<void, NiroJobAccess>(
            {
                query: (data) => ({
                    url: `/nirovision/remove-contractor-access`,
                    method: 'POST',
                    body: data,
                }),
                invalidatesTags: ['Nirovision'],
            },
        ),
        getNirovisionAccessRequests: builder.query<
            NirovisionAccessRequest[],
            void
        >({
            query: () => '/nirovision/access-requests',
            providesTags: ['Nirovision', 'SignIn'],
        }),
        getNirovisionAccessRequest: builder.query<
            NirovisionAccessRequest,
            number
        >({
            query: (id) => `/nirovision/access-requests/${id}`,
            providesTags: ['Nirovision'],
        }),
        getNirovisionAccessRequestUsers: builder.query<
            NirovisionAccessRequestUser[],
            number
        >({
            query: (id) => `/nirovision/access-requests/${id}/users`,
            providesTags: ['Nirovision'],
        }),
        getNirovisionAccessRequestFaces: builder.query<
            NirovisionAccessRequestFace[],
            number
        >({
            query: (id) => `/nirovision/access-requests/${id}/faces`,
            providesTags: ['Nirovision'],
        }),
        getNirovisionAccessRequestFaceImage: builder.query<
            string,
            { id: number; faceId: string }
        >({
            query: ({ id, faceId }) => ({
                url: `/nirovision/access-requests/${id}/faces/${faceId}`,
                responseHandler: async (response: Response) => {
                    const blob = await response.blob();
                    return URL.createObjectURL(blob);
                },
            }),
            providesTags: ['Nirovision'],
        }),
        getNirovisionAccessRequestEventImage: builder.query<string, number>({
            query: (id) => ({
                url: `/nirovision/access-requests/${id}/event-image`,
                responseHandler: async (response: Response) => {
                    const blob = await response.blob();
                    return URL.createObjectURL(blob);
                },
            }),
            providesTags: ['Nirovision'],
        }),
        respondToNirovisionAccessRequest: builder.mutation<
            NirovisionAccessRequest,
            {
                id: number;
                action: NirovisionAccessRequestAction;
                accessMode?: NirovisionAccessMode;
                profilePictureFaceId?: string;
                useEventImage?: boolean;
                userId?: number;
                email?: string;
            }
        >({
            query: ({ id, ...body }) => ({
                url: `/nirovision/access-requests/${id}/respond`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Nirovision', 'Reservations'],
        }),
        //Xero network key for hosts
        addXeroNetworkKey: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/xero`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['XeroSettings'],
        }),
        getXeroNetworkKey: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['XeroSettings', 'SignIn'],
        }),
        // Contractors
        getIndividualContractor: builder.query<Contractor, number>({
            query: (id) => `/contractors/${id}`,
            providesTags: ['Contractors', 'Profile'],
        }),
        getContractors: builder.query<Contractor[], void>({
            query: () => '/contractors',
            providesTags: ['Contractors'],
        }),
        getOwnerContractors: builder.query<OwnerContractor[], number>({
            query: (id) => `/owners/${id}/contractors`,
            providesTags: ['Contractors'],
        }),
        getIndividualOwnerContractor: builder.query<
            OwnerContractor,
            [number, number]
        >({
            query: (ids) => `/owners/${ids[0]}/contractors/${ids[1]}`,
            providesTags: ['Contractors'],
        }),
        editOwnerContractor: builder.mutation<
            OwnerContractor,
            [number, InviteContractor]
        >({
            query: (data) => ({
                url: `/owners/${data[0]}/contractors/${data[1].contractorId}`,
                method: 'PATCH',
                body: data[1],
            }),
            invalidatesTags: ['Contractors'],
        }),
        getContractorClients: builder.query<any, number>({
            query: (id) => `/contractors/${id}/clients`,
            providesTags: ['Contractors'],
        }),
        getContractorInvites: builder.query<any, void>({
            query: () => `/contractors/invites`,
            providesTags: ['Contractors'],
        }),
        sendContractorInvite: builder.mutation<void, InviteContractor>({
            query: (data) => ({
                url: `/contractors${
                    data.contractorId ? `/${data.contractorId}/` : '/'
                }invite`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Contractors'],
        }),
        cancelContractorInvite: builder.mutation<void, number>({
            query: (id) => ({
                url: `/contractors/invites/${id}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        acceptContractorInvite: builder.mutation<void, number>({
            query: (id) => ({
                url: `/contractors/invites/${id}/accept`,
                method: 'POST',
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        declineContractorInvite: builder.mutation<void, number>({
            query: (id) => ({
                url: `/contractors/invites/${id}/decline`,
                method: 'POST',
            }),
            invalidatesTags: ['Contractors'],
        }),
        createJobRequest: builder.mutation<Job, JobRequest>({
            query: (data) => ({
                url: `/contractors/jobs`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        createRecurringJobRequest: builder.mutation<any, any>({
            query: (data) => ({
                url: `/contractors/recurring-jobs`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),

        updateRecurringJobRequest: builder.mutation<any, any>({
            query: (data) => ({
                url: `/contractors/recurring-jobs/${data[0]}`,
                method: 'PATCH',
                body: data[1],
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        deleteRecurringJob: builder.mutation<any, any>({
            query: (id) => ({
                url: `/contractors/recurring-jobs/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),

        getRecurringJobs: builder.query<
            RecurringJob[],
            {
                id?: number;
                enabled?: boolean;
                moduleIds?: number[];
            }
        >({
            query: (args) => ({
                url: '/contractors/recurring-jobs',
                params: { ...args },
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        getJobs: builder.query<
            Job[],
            {
                bookingId?: number;
                status?:
                    | 'pending'
                    | 'accepted'
                    | 'declined'
                    | 'cancelled'
                    | 'completed'
                    | 'pendingCompletion';
                recurringId?: number;
            }
        >({
            query: (args) => ({
                url: '/contractors/jobs',
                params: { ...args },
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),

        getIndividualJob: builder.query<Job, number>({
            query: (id) => ({
                url: `/contractors/jobs/${id}`,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        getIndividualRecurringJob: builder.query<any, any>({
            query: (id) => ({
                url: `/contractors/recurring-jobs/${id}`,
            }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        editJob: builder.mutation<Job, [number, UpdateJob]>({
            query: (data) => ({
                url: `/contractors/jobs/${data[0]}`,
                method: 'PATCH',
                body: data[1],
            }),
            invalidatesTags: [
                'Contractors',
                'Reservations',
                'Jobs',
                'Nirovision',
            ],
        }),
        acceptJob: builder.mutation<Job, JobAssignee>({
            query: ({ id, ...body }) => ({
                url: `/contractors/jobs/${id}/accept`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        reassignJob: builder.mutation<Job, JobAssignee>({
            query: ({ id, ...body }) => ({
                url: `/contractors/jobs/${id}/reassign`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Contractors', 'Jobs', 'Nirovision'],
        }),
        declineJob: builder.mutation<Job, number>({
            query: (id) => ({
                url: `/contractors/jobs/${id}/decline`,
                method: 'POST',
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        cancelJob: builder.mutation<Job, number>({
            query: (id) => ({
                url: `/contractors/jobs/${id}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        completeJob: builder.mutation<Job, number>({
            query: (id) => ({
                url: `/contractors/jobs/${id}/complete`,
                method: 'POST',
            }),
            invalidatesTags: ['Contractors', 'Reservations', 'Jobs'],
        }),
        addContractorNotes: builder.mutation<Job, ContractorNotes>({
            query: ({ id, ...data }) => ({
                url: `/contractors/jobs/${id}/contractor-notes`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Contractors', 'Jobs'],
        }),
        addJobPhotos: builder.mutation<Media[], FormData>({
            query: (data) => ({
                url: `/contractors/jobs/photos`,
                method: 'POST',
                body: data,
                formData: true,
            }),
            invalidatesTags: ['Contractors', 'Jobs'],
        }),
        addJobCompletion: builder.mutation<Job, [number, JobCompletion]>({
            query: ([id, data]) => ({
                url: `/contractors/jobs/${id}/job-completion`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Contractors', 'Jobs'],
        }),
        getPricingTemplates: builder.query<PricingTemplate[], number>({
            query: (id) => `/contractors/${id}/pricing-templates`,
            providesTags: ['Contractors'],
        }),
        createPricingTemplate: builder.mutation<
            PricingTemplate,
            [number, PricingTemplate]
        >({
            query: ([id, data]) => ({
                url: `/contractors/${id}/pricing-templates`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Contractors'],
        }),
        updatePricingTemplate: builder.mutation<
            PricingTemplate,
            [number, number, PricingTemplate]
        >({
            query: ([contractorId, templateId, data]) => ({
                url: `/contractors/${contractorId}/pricing-templates/${templateId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Contractors'],
        }),
        deletePricingTemplate: builder.mutation<void, [number, number]>({
            query: ([contractorId, templateId]) => ({
                url: `/contractors/${contractorId}/pricing-templates/${templateId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Contractors'],
        }),
        reorderPricingTemplates: builder.mutation<
            PricingTemplate[],
            [number, PricingTemplateOrderRequest]
        >({
            query: ([contractorId, body]) => ({
                url: `/contractors/${contractorId}/pricing-templates/order`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['Contractors'],
        }),

        // Set Menus

        getSetMenus: builder.query<SetMenu[], void>({
            query: () => `/set-menus`,
            providesTags: ['SetMenus'],
        }),
        getBookingSetMenus: builder.query<
            BookingSetMenu[],
            { status?: string }
        >({
            query: ({ status }) => ({
                url: `/set-menus/requests`,
                params: { status },
            }),
            providesTags: ['BookingSetMenus'],
        }),
        getIndividualBookingSetMenu: builder.query<BookingSetMenu, number>({
            query: (id) => ({
                url: `/set-menus/requests/${id}`,
            }),
            providesTags: ['BookingSetMenus'],
        }),
        createSetMenu: builder.mutation<SetMenu, SetMenu>({
            query: (data) => ({
                url: `/set-menus`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['SetMenus'],
        }),
        updateSetMenu: builder.mutation<SetMenu, [number, SetMenu]>({
            query: ([setMenuId, data]) => ({
                url: `/set-menus/${setMenuId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['SetMenus'],
        }),
        createBookingSetMenu: builder.mutation<BookingSetMenu, JobRequest>({
            query: (data) => ({
                url: `/set-menus/requests`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['BookingSetMenus', 'Reservations'],
        }),
        updateBookingSetMenu: builder.mutation<
            BookingSetMenu,
            [number, UpdateBookingSetMenu]
        >({
            query: ([bookingSetMenuId, data]) => ({
                url: `/set-menus/requests/${bookingSetMenuId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['BookingSetMenus', 'Reservations'],
        }),
        declineBookingSetMenu: builder.mutation<BookingSetMenu, number>({
            query: (bookingSetMenuId) => ({
                url: `/set-menus/requests/${bookingSetMenuId}/decline`,
                method: 'POST',
            }),
            invalidatesTags: ['BookingSetMenus', 'Reservations'],
        }),
        cancelBookingSetMenu: builder.mutation<BookingSetMenu, number>({
            query: (bookingSetMenuId) => ({
                url: `/set-menus/requests/${bookingSetMenuId}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: ['BookingSetMenus', 'Reservations'],
        }),
        deleteSetMenu: builder.mutation<void, number>({
            query: (setMenuId) => ({
                url: `/set-menus/${setMenuId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['SetMenus'],
        }),

        // WaitList Requests

        submitWaitListRequest: builder.mutation<any, any>({
            query: (data) => ({
                url: `/wait-list`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [
                'Reservations',
                'Assets',
                'SignIn',
                'Reports',
                'Nirovision',
            ],
        }),

        getIndividualWaitListRequest: builder.query<
            WaitListRequestDetailed,
            any
        >({
            query: (id) => `/wait-list/${id}`,
            providesTags: ['WaitListRequests', 'Profile'],
        }),

        updateWaitListRequest: builder.mutation<
            WaitListRequestDetailed,
            [number, UpdateWaitListRequest]
        >({
            query: ([id, data]) => ({
                url: `/wait-list/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['WaitListRequests', 'Profile', 'Reservations'],
        }),

        getAllWaitlistRequests: builder.query<any, any>({
            query: (args) => ({ url: `/wait-list`, params: { ...args } }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['Reservations', 'SignIn'],
        }),

        cancelWaitListRequest: builder.mutation<void, number>({
            query: (id) => ({
                url: `/wait-list/${id}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: ['WaitListRequests', 'Reservations'],
        }),

        archiveWaitListRequest: builder.mutation<void, number>({
            query: (id) => ({
                url: `/wait-list/${id}/archive`,
                method: 'POST',
            }),
            invalidatesTags: ['WaitListRequests', 'Reservations'],
        }),

        // Expense Claims
        getExpenseClaims: builder.query<any, any>({
            query: (args) => ({ url: '/expenses', params: { ...args } }),
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['ExpenseClaims', 'SignIn', 'Reservations'],
        }),
        addExpenseClaim: builder.mutation<any, any>({
            query: (data) => ({
                url: `/expenses`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ExpenseClaims', 'Reservations'],
        }),
        deleteExpenseClaim: builder.mutation<any, any>({
            query: (id) => ({
                url: `/expenses/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ExpenseClaims', 'Reservations'],
        }),
        editExpenseClaim: builder.mutation<any, any>({
            query: (data) => ({
                url: `/expenses/${data?.[0]}`,
                method: 'PATCH',
                body: data?.[1],
            }),
            invalidatesTags: ['ExpenseClaims', 'Reservations'],
        }),
        addExpenseAttachments: builder.mutation<any, any>({
            query: (data) => ({
                url: `/expenses/attachments`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ExpenseClaims'],
        }),
        deleteExpenseAttachment: builder.mutation<any, any>({
            query: (uuid) => ({
                url: `/expenses/attachments/${uuid}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ExpenseClaims'],
        }),
        submitHostPaymentDetails: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data?.[0]}/bank-account`,
                method: 'POST',
                body: data?.[1],
            }),
            invalidatesTags: ['PayoutDetails'],
        }),
        getHostPayoutDetails: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['PayoutDetails', 'SignIn'],
        }),
        setUserAccessControl: builder.mutation<any, any>({
            query: (data) => ({
                url: `/guests/${data?.[0]}/employee-access`,
                method: 'POST',
                body: data?.[1],
            }),
            invalidatesTags: ['accessControl', 'SignIn'],
        }),
        getUserAccessControl: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['accessControl', 'SignIn'],
        }),
        getBillingExclusions: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['billingexclusions', 'SignIn'],
        }),
        addBillingExclusion: builder.mutation<any, any>({
            query: (data) => ({
                url: `/owners/${data[0]}/billing-exclusions`,
                method: 'POST',
                body: data[1],
            }),
            invalidatesTags: ['billingexclusions'],
        }),
        getOwnerPreferences: builder.query<OwnerPreferences, number>({
            query: (id) => `/owners/${id}/preferences`,
            providesTags: ['Settings', 'SignIn'],
        }),
        updateOwnerPreferences: builder.mutation<
            OwnerPreferences,
            [number, OwnerPreferences]
        >({
            query: (data) => ({
                url: `/owners/${data[0]}/preferences`,
                method: 'PATCH',
                body: data[1],
            }),
            invalidatesTags: ['Settings'],
        }),
        getContractorPreferences: builder.query<ContractorPreferences, number>({
            query: (id) => `/contractors/${id}/preferences`,
            providesTags: ['Settings', 'SignIn'],
        }),
        updateContractorPreferences: builder.mutation<
            ContractorPreferences,
            [number, ContractorPreferences]
        >({
            query: (data) => ({
                url: `/contractors/${data[0]}/preferences`,
                method: 'PATCH',
                body: data[1],
            }),
            invalidatesTags: ['Settings'],
        }),

        //Experience Reviews
        addReviewPhotos: builder.mutation<any, any>({
            query: (data) => ({
                url: `/reviews/photos`,
                method: 'POST',
                body: data,
            }),
        }),

        addReview: builder.mutation<any, any>({
            query: (data) => ({
                url: `/reviews`,
                method: 'POST',
                body: data,
            }),
        }),
        updateReview: builder.mutation<any, { id: number; data: any }>({
            query: ({ id, data }) => ({
                url: `/reviews/${id}`,
                method: 'PATCH',
                body: data,
            }),
        }),
        getUserReviewForBooking: builder.query<any, number>({
            query: (bookingId) => `reviews/booking/${bookingId}/user`,
            providesTags: ['UserReview'],
        }),
        getReviewsByModule: builder.query<any, number>({
            query: (moduleId) => `reviews/module/${moduleId}`,
        }),

        getReviews: builder.query<
            any,
            { moduleId?: number; bookingId?: number; createdBy?: number } | void
        >({
            query: (args) => ({
                url: `/reviews`,
                params: { ...(args ?? {}) },
            }),
            providesTags: ['UserReview', 'SignIn'],
        }),

        addHostReply: builder.mutation<
            UserReview,
            { reviewId: number; replyText: string }
        >({
            query: ({ reviewId, replyText }) => ({
                url: `/reviews/${reviewId}/reply`,
                method: 'POST',
                body: { replyText },
            }),
            invalidatesTags: ['UserReview'],
        }),

        updateHostReply: builder.mutation<
            UserReview,
            { reviewId: number; replyText: string }
        >({
            query: ({ reviewId, replyText }) => ({
                url: `/reviews/${reviewId}/reply`,
                method: 'PATCH',
                body: { replyText },
            }),
            invalidatesTags: ['UserReview'],
        }),

        getGuestReviews: builder.query<
            GuestReview[],
            { bookingId?: number } | void
        >({
            query: (args) => ({
                url: `/guest-reviews`,
                params: { ...(args ?? {}) },
            }),
            providesTags: ['GuestReview'],
        }),

        getGuestReviewsByGuest: builder.query<GuestReview[], number>({
            query: (guestId) => `/guest-reviews/guest/${guestId}`,
            providesTags: ['GuestReview'],
        }),

        addGuestReview: builder.mutation<GuestReview, any>({
            query: (data) => ({
                url: `/guest-reviews`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['GuestReview'],
        }),

        updateGuestReview: builder.mutation<
            GuestReview,
            { id: number; data: any }
        >({
            query: ({ id, data }) => ({
                url: `/guest-reviews/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['GuestReview'],
        }),

        getGuestProfile: builder.query<GuestProfile, number>({
            query: (guestId) => `/guests/${guestId}/profile`,
            providesTags: ['GuestReview'],
        }),

        getGuestUserProfile: builder.query<GuestUserProfile, number>({
            query: (userId) => `/guest-users/${userId}/profile`,
            providesTags: ['GuestReview'],
        }),

        getOwnerProfile: builder.query<OwnerProfile, number>({
            query: (ownerId) => `/owners/${ownerId}/profile`,
            providesTags: ['Owners', 'Assets', 'Discounts'],
        }),
        updateOwnerProfile: builder.mutation<
            OwnerProfile,
            UpdateOwnerProfileRequest
        >({
            query: (data) => {
                const { ownerId, ...body } = data;
                return {
                    url: `/owners/${data.ownerId}/profile`,
                    method: 'PATCH',
                    body,
                };
            },
            invalidatesTags: ['Owners'],
        }),
        createOwnerLogo: builder.mutation<
            Media,
            { ownerId: number; data: FormData }
        >({
            query: ({ ownerId, data }) => ({
                url: `/owners/${ownerId}/logo`,
                method: 'POST',
                body: data,
                formData: true,
            }),
            invalidatesTags: ['Owners', 'Profile'],
        }),
        getModuleAccess: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['moduleAccess', 'Reservations', 'Assets'],
        }),

        addModuleAccess: builder.mutation<
            any,
            [
                number,
                {
                    module?: { id: number };
                    users: { id: number }[];
                    roles: (typeof ROLE_OPTIONS)[number][];
                },
            ]
        >({
            query: ([guestId, data]) => ({
                url: `/guests/${guestId}/module-access`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['moduleAccess', 'Reservations', 'Assets'],
        }),

        getIndividualModuleAccess: builder.query<any, any>({
            query: (url) => `${url}`,
            transformResponse: (response: any) => {
                return response;
            },
            providesTags: ['moduleAccess', 'Reservations', 'Assets'],
        }),

        deleteIndividualModuleAccess: builder.mutation<any, any>({
            query: ({ guestId, id }) => ({
                url: `/guests/${guestId}/module-access/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['moduleAccess', 'Reservations', 'Assets'],
        }),

        editModuleAccess: builder.mutation<
            any,
            { guestId: number; id: number; data: any }
        >({
            query: ({ guestId, id, data }) => ({
                url: `/guests/${guestId}/module-access/${id}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['moduleAccess', 'Reservations', 'Assets'],
        }),

        getIndividualCreditHold: builder.query<CreditHold, { id: number }>({
            query: ({ id }) => ({
                url: `credit-holds/${id}`,
            }),
            providesTags: ['CreditHolds', 'SignIn'],
        }),

        getCreditHolds: builder.query<CreditHold[], void>({
            query: (params) => ({
                url: 'credit-holds',
                params,
            }),
            providesTags: ['CreditHolds', 'SignIn'],
        }),

        addCreditHold: builder.mutation<CreditHold, { guest: { id: number } }>({
            query: (body) => ({
                url: 'credit-holds',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['CreditHolds', 'Guest'],
        }),

        removeCreditHold: builder.mutation<CreditHold, { id: number }>({
            query: ({ id }) => ({
                url: `credit-holds/${id}/remove`,
                method: 'POST',
            }),
            invalidatesTags: ['CreditHolds', 'Guest'],
        }),

        cancelReservationsByCreditHold: builder.mutation<
            CreditHold,
            { id: number }
        >({
            query: ({ id }) => ({
                url: `credit-holds/${id}/cancel-bookings`,
                method: 'POST',
            }),
            invalidatesTags: ['CreditHolds', 'Reservations'],
        }),

        updateRsvpConfig: builder.mutation<
            void,
            { bookingId: number; config: RsvpConfig }
        >({
            query: ({ bookingId, config }) => ({
                url: `rsvps/config/${bookingId}`,
                method: 'POST',
                body: config,
            }),
            invalidatesTags: ['Reservations'],
        }),

        getRsvpRequests: builder.query<RsvpRequest[], any>({
            query: () => ({
                url: 'rsvps/requests',
            }),
            providesTags: ['Reservations', 'SignIn'],
        }),

        respondToRsvp: builder.mutation<
            void,
            { bookingId: number; rsvpId: string; status: RsvpStatus }
        >({
            query: (body) => ({
                url: `rsvps/respond`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Reservations'],
        }),

        sendRsvpReminders: builder.mutation<
            void,
            { bookingId: number; rsvpIds: string[] }
        >({
            query: (body) => ({
                url: `rsvps/reminders`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Reservations'],
        }),

        addItineraryItem: builder.mutation<
            ItineraryItemDto,
            [number, ItineraryItemPayload]
        >({
            query: ([bookingId, data]) => ({
                url: `/bookings/${bookingId}/itinerary-items`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['Reservations'],
        }),

        updateItineraryItem: builder.mutation<
            ItineraryItemDto,
            [number, number, ItineraryItemPayload]
        >({
            query: ([bookingId, itemId, data]) => ({
                url: `/bookings/${bookingId}/itinerary-items/${itemId}`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: ['Reservations'],
        }),

        deleteItineraryItem: builder.mutation<void, [number, number]>({
            query: ([bookingId, itemId]) => ({
                url: `/bookings/${bookingId}/itinerary-items/${itemId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Reservations'],
        }),

        addItineraryAttachments: builder.mutation<
            ItineraryItemAttachment[],
            FormData
        >({
            query: (data) => ({
                url: `/bookings/itinerary-items/attachments`,
                method: 'POST',
                body: data,
            }),
        }),

        deleteItineraryAttachment: builder.mutation<void, [number, string]>({
            query: ([bookingId, attachmentUuid]) => ({
                url: `/bookings/${bookingId}/itinerary-items/attachments/${attachmentUuid}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Reservations'],
        }),

        getAllDevices: builder.query<Device[], void>({
            query: () => '/devices',
            providesTags: ['Devices', 'SignIn'],
        }),
        getDevice: builder.query<Device, number>({
            query: (id) => `/devices/${id}`,
            providesTags: ['Devices', 'SignIn'],
        }),
        createDevice: builder.mutation<Device, CreateDevice>({
            query: (body) => ({
                url: '/devices',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Devices'],
        }),
        updateDevice: builder.mutation<Device, [number, UpdateDevice]>({
            query: ([id, body]) => ({
                url: `/devices/${id}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['Devices'],
        }),
        removeDevice: builder.mutation<void, number>({
            query: (id) => ({
                url: `/devices/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Devices'],
        }),
        getAllVehicles: builder.query<Vehicle[], void>({
            query: () => '/vehicles',
            providesTags: ['Vehicles', 'SignIn'],
        }),
    }),
});
export const {
    useGetAllAssetsQuery,
    useGetWishlistQuery,
    useAddToWishlistMutation,
    useRemoveFromWishlistMutation,
    useGetIndividualOwnerAssetsQuery,
    useGetIndividualAssetQuery,
    useAddAssetMutation,
    useCreateExperienceMutation,
    useGetOwnerExperienceQuery,
    useLazyGetOwnerExperiencesQuery,
    usePublishExperienceMutation,
    useUpdateExperienceMutation,
    useAddPhotosMutation,
    useAddVideoThumbnailMutation,
    useAddGuideThumbnailMutation,
    useAddFloorPlansMutation,
    useEditPhotosMutation,
    useEditAssetMutation,
    useDeleteAssetMutation,
    useCalculatePriceMutation,
    useGetSpaceBookingsCountMutation,
    useSignInMutation,
    useSignUpMutation,
    useRefreshMutation,
    useSignOutMutation,
    useGetIndividualOwnerQuery,
    useGetCompanyOwnerInfoQuery,
    useEditCompanyMutation,
    useGetCompanyGuestsInfoQuery,
    useGetProfileCompanyInfoQuery,
    useEditProfileDetailsMutation,
    useGetProfileDetailsQuery,
    useDeleteProfilePictureMutation,
    useCreateProfilePictureMutation,
    useValidateProfilePictureMutation,
    useGetUserPreferencesQuery,
    useUpdateUserPreferencesMutation,
    useAddIndividualOwnerUsersMutation,
    useAddIndividualGuestUsersMutation,
    useAddIndividualContractorUsersMutation,
    useAddIndividual7DayAdminMutation,
    useAddNewClientMutation,
    useGetAllClientsQuery,
    useGetIndividualClientQuery,
    useEditClientMutation,
    useDeleteClientMutation,
    useCreateChatEnquiryMutation,
    useCreateReservationChatMutation,
    useCreateJobChatMutation,
    useJoinBookingChannelMutation,
    useJoinJobChannelMutation,
    useGetChatUsersQuery,
    useGetChatTokenQuery,
    useGetMessageTemplatesQuery,
    useCreateMessageTemplateMutation,
    useUpdateMessageTemplateMutation,
    useDeleteMessageTemplateMutation,
    useGetUserListQuery,
    useGetIndividualUserInfoQuery,
    useEditUserMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useVerifyEmailMutation,
    useSendVerificationEmailMutation,
    useChangePasswordMutation,
    useSubmitReservationMutation,
    useGetAllReservationsQuery,
    useGetAllReservationsDetailedQuery,
    useGetIndividualReservationQuery,
    useGetReservationConflictsMutation,
    useGetAllGuestsQuery,
    useGetAllGuestAttendeesQuery,
    useGetAllExternalAttendeesQuery,
    useGetAllOwnerAttendeesQuery,
    useGetIndividualGuestsQuery,
    useApproveReservationMutation,
    useApproveBulkReservationsMutation,
    useCancelReservationMutation,
    useCancelBulkReservationsMutation,
    useAddOwnerNotesMutation,
    useAddAttachmentsMutation,
    useDeleteAttachmentMutation,
    useDeclineReservationMutation,
    useDeclineBulkReservationsMutation,
    useGetAssetCalendarAvailabilityQuery,
    useEditReservationMutation,
    useGetReportQuery,
    useGetExpenseStatsQuery,
    useGetAddOnStatsQuery,
    useGetJobStatsQuery,
    useGetRsvpSentStatsQuery,
    useGetRsvpResponseStatsQuery,
    useGetGuestUserStatsQuery,
    useGetExternalGuestAttendeeStatsQuery,
    useGetTotalBookingsStatsQuery,
    useGetNewBookingStatsQuery,
    useGetOccupancyQuery,
    useSendInviteMutation,
    useGetBookingCapListQuery,
    useAddIndividualBookingCapMutation,
    useGetIndividualBookingCapQuery,
    useGetBookingCapAvailabilityMutation,
    useEditBookingCapMutation,
    useDeleteBookingCapMutation,
    useGetFeeListQuery,
    useAddIndividualFeeMutation,
    useGetIndividualFeeQuery,
    useEditFeeMutation,
    useDeleteFeeMutation,
    useGetTeamListQuery,
    useAddTeamMutation,
    useGetIndividualTeamQuery,
    useEditTeamMutation,
    useDeleteTeamMutation,
    useGetPolicyListQuery,
    useGetIndividualPolicyQuery,
    useAddPolicyMutation,
    useEditPolicyMutation,
    useDeletePolicyMutation,
    useCalculateFeesMutation,
    useGetAvailabilityQuery,
    useAiReservationRequestMutation,
    useAiGuestReservationRequestMutation,
    useValidateCustomServiceMutation,
    useAiDiscountsRequestMutation,
    useSubmitRecurringReservationMutation,
    useGetRecurringReservationQuery,
    useCancelRecurringReservationMutation,
    useSubmitDiscountMutation,
    useGetDiscountsQuery,
    useGetIndividualDiscountQuery,
    useDeleteDiscountMutation,
    useDisableDiscountMutation,
    useGetBillingAccountQuery,
    useValidateImportGuestUsersMutation,
    useImportGuestUsersMutation,
    useEditPlatformSettingsMutation,
    useGetPlatformSettingsQuery,
    useGetReleaseSettingsQuery,
    useEditReleaseSettingsMutation,
    useGetBillingReportQuery,
    useGetIndividualBillQuery,
    useSetBillingAccountMutation,
    useLazyGetXeroConsentUrlQuery,
    useGetXeroConnectionQuery,
    useEditXeroSettingsMutation,
    useGetXeroSettingsQuery,
    useGetXeroAccountsQuery,
    useGetEarningsQuery,
    useGetContractorsQuery,
    useGetOwnerContractorsQuery,
    useGetIndividualOwnerContractorQuery,
    useEditOwnerContractorMutation,
    useGetContractorClientsQuery,
    useGetContractorInvitesQuery,
    useGetIndividualContractorQuery,
    useSendContractorInviteMutation,
    useCancelContractorInviteMutation,
    useAcceptContractorInviteMutation,
    useDeclineContractorInviteMutation,
    useCreateJobRequestMutation,
    useGetJobsQuery,
    useGetRecurringJobsQuery,
    useGetIndividualRecurringJobQuery,
    useGetIndividualJobQuery,
    useEditJobMutation,
    useAcceptJobMutation,
    useReassignJobMutation,
    useDeclineJobMutation,
    useCancelJobMutation,
    useCompleteJobMutation,
    useAddContractorNotesMutation,
    useAddJobCompletionMutation,
    useAddJobPhotosMutation,
    useGetPricingTemplatesQuery,
    useCreatePricingTemplateMutation,
    useUpdatePricingTemplateMutation,
    useReorderPricingTemplatesMutation,
    useDeletePricingTemplateMutation,
    useGetSetMenusQuery,
    useGetBookingSetMenusQuery,
    useGetIndividualBookingSetMenuQuery,
    useCreateSetMenuMutation,
    useUpdateSetMenuMutation,
    useCreateBookingSetMenuMutation,
    useUpdateBookingSetMenuMutation,
    useDeclineBookingSetMenuMutation,
    useCancelBookingSetMenuMutation,
    useDeleteSetMenuMutation,
    useGetExpenseClaimsQuery,
    useAddExpenseClaimMutation,
    useDeleteExpenseClaimMutation,
    useEditExpenseClaimMutation,
    useAddExpenseAttachmentsMutation,
    useDeleteExpenseAttachmentMutation,
    useAddNirovisionConnectionMutation,
    useGetNirovisionConnectionQuery,
    useGetNirovisionLabelsQuery,
    useGetNirovisionLocationsQuery,
    useGetNirovisionAccessListQuery,
    useGetNirovisionJobAccessListQuery,
    useProvideNirovisionAccessMutation,
    useProvideNirovisionContractorAccessMutation,
    useRemoveNirovisionAccessMutation,
    useRemoveNirovisionContractorAccessMutation,
    useGetNirovisionAccessRequestsQuery,
    useGetNirovisionAccessRequestQuery,
    useGetNirovisionAccessRequestUsersQuery,
    useGetNirovisionAccessRequestFacesQuery,
    useGetNirovisionAccessRequestFaceImageQuery,
    useGetNirovisionAccessRequestEventImageQuery,
    useRespondToNirovisionAccessRequestMutation,
    useAddPresignedVideoUrlMutation,
    useUploadVideoMutation,
    useAddPresignedGuideUrlMutation,
    useUploadGuideMutation,
    useResendCodeMutation,
    useGetAIConversationIdMutation,
    useGetAllConversationsQuery,
    useGetIndividualConversationQuery,
    useSubmitHostPaymentDetailsMutation,
    useGetHostPayoutDetailsQuery,
    useSetUserAccessControlMutation,
    useGetUserAccessControlQuery,
    useAddXeroNetworkKeyMutation,
    useGetXeroNetworkKeyQuery,
    useGetBillingExclusionsQuery,
    useAddBillingExclusionMutation,
    useAddReviewPhotosMutation,
    useAddReviewMutation,
    useUpdateReviewMutation,
    useAddHostReplyMutation,
    useUpdateHostReplyMutation,
    useGetGuestReviewsQuery,
    useGetGuestReviewsByGuestQuery,
    useAddGuestReviewMutation,
    useUpdateGuestReviewMutation,
    useGetGuestProfileQuery,
    useGetGuestUserProfileQuery,
    useGetOwnerProfileQuery,
    useUpdateOwnerProfileMutation,
    useCreateOwnerLogoMutation,
    useGetReviewsQuery,
    useSubmitWaitListRequestMutation,
    useGetAllWaitlistRequestsQuery,
    useGetIndividualWaitListRequestQuery,
    useUpdateWaitListRequestMutation,
    useCancelWaitListRequestMutation,
    useArchiveWaitListRequestMutation,
    useGetReviewsByModuleQuery,
    useGetUserReviewForBookingQuery,
    useCreateRecurringJobRequestMutation,
    useUpdateRecurringJobRequestMutation,
    useDeleteRecurringJobMutation,
    useGetOwnerPreferencesQuery,
    useUpdateOwnerPreferencesMutation,
    useGetContractorPreferencesQuery,
    useUpdateContractorPreferencesMutation,
    useGetModuleAccessQuery,
    useAddModuleAccessMutation,
    useGetIndividualModuleAccessQuery,
    useDeleteIndividualModuleAccessMutation,
    useEditModuleAccessMutation,
    useGetIndividualCreditHoldQuery,
    useGetCreditHoldsQuery,
    useAddCreditHoldMutation,
    useRemoveCreditHoldMutation,
    useCancelReservationsByCreditHoldMutation,
    useReassignBulkReservationsMutation,
    useApplyDiscountToBookingMutation,
    useUpdateRsvpConfigMutation,
    useGetRsvpRequestsQuery,
    useRespondToRsvpMutation,
    useSendRsvpRemindersMutation,
    useAddItineraryItemMutation,
    useUpdateItineraryItemMutation,
    useDeleteItineraryItemMutation,
    useAddItineraryAttachmentsMutation,
    useDeleteItineraryAttachmentMutation,
    useGetAllDevicesQuery,
    useGetDeviceQuery,
    useCreateDeviceMutation,
    useUpdateDeviceMutation,
    useRemoveDeviceMutation,
    useGetAllVehiclesQuery,
    useCheckSelectedPeriodAvailabilityMutation,
} = sevenDayApi;
